import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { getMovieById, getRelatedMovies, categoryNames } from "@/mocks/movies";

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!movie) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
        </span>
        <h1 className="text-2xl font-bold text-foreground-950 font-heading">影片未找到</h1>
        <p className="text-foreground-500 mt-2 mb-6">该影片不存在或已下架</p>
        <Link
          to="/"
          className="inline-flex px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.title,
    description: movie.description,
    image: movie.image,
    director: { "@type": "Person", name: movie.director },
    actor: movie.actors.map((a) => ({ "@type": "Person", name: a })),
    dateCreated: `${movie.year}`,
    genre: movie.genres,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: movie.rating,
      bestRating: "10",
      worstRating: "0",
      ratingCount: Math.floor(Math.random() * 50000) + 10000,
    },
    url: window.location.href,
    inLanguage: "zh-CN",
    countryOfOrigin: movie.region,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin },
      {
        "@type": "ListItem",
        position: 2,
        name: categoryNames[movie.category] || movie.category,
        item: `${window.location.origin}/category/${movie.category}`,
      },
      { "@type": "ListItem", position: 3, name: movie.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Breadcrumb
        items={[
          { label: categoryNames[movie.category] || movie.category, path: `/category/${movie.category}` },
          { label: movie.title },
        ]}
      />

      <article className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
        {/* 影片详情头部 */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 mb-10">
          <div className="w-full md:w-[300px] flex-shrink-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
              <img
                src={movie.image}
                alt={movie.title}
                title={`${movie.title} - 蓝莓影院高清免费在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              {movie.title}
            </h1>

            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="flex items-center gap-1 text-accent-500 font-semibold text-lg">
                <i className="ri-star-fill"></i> {movie.rating}
              </span>
              <span className="text-foreground-400">|</span>
              <span className="text-sm text-foreground-600">{movie.year}</span>
              <span className="text-foreground-400">|</span>
              <span className="text-sm text-foreground-600">{movie.duration}</span>
              <span className="text-foreground-400">|</span>
              <span className="text-sm text-foreground-600">{movie.region}</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/category/${movie.category}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-accent-200 transition-colors"
                >
                  {g}
                </Link>
              ))}
              {movie.isNew && (
                <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
                  最新上线
                </span>
              )}
              {movie.isHot && (
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium whitespace-nowrap">
                  热门推荐
                </span>
              )}
            </div>

            <div className="space-y-2.5 text-sm text-foreground-700 mb-6">
              <p>
                <strong className="text-foreground-950 inline-block w-14">导演：</strong>
                {movie.director}
              </p>
              <p>
                <strong className="text-foreground-950 inline-block w-14">主演：</strong>
                {movie.actors.join(" / ")}
              </p>
              <p>
                <strong className="text-foreground-950 inline-block w-14">类型：</strong>
                {movie.genres.join(" / ")}
              </p>
              <p>
                <strong className="text-foreground-950 inline-block w-14">地区：</strong>
                {movie.region}
              </p>
              <p>
                <strong className="text-foreground-950 inline-block w-14">更新日期：</strong>
                {movie.updateDate}
              </p>
            </div>

            <div className="flex gap-3 flex-wrap">
              <Link
                to="#"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <i className="ri-play-circle-fill text-lg"></i>
                立即播放
              </Link>
              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors">
                <i className="ri-heart-line"></i>
                收藏
              </button>
              <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors">
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>
        </div>

        {/* 剧情简介 */}
        <section className="mb-10">
          <h2 className="text-lg font-bold text-foreground-950 font-heading mb-3">剧情简介</h2>
          <div className="p-5 rounded-xl bg-background-100">
            <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
          </div>
        </section>

        {/* 相关推荐 */}
        {relatedMovies.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">相关推荐</h2>
              <Link
                to={`/category/${movie.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                更多{movie.category === "dianying" ? "电影" : movie.category === "dianshiju" ? "电视剧" : movie.category === "dongman" ? "动漫" : "综艺"} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5"
              data-product-shop
            >
              {relatedMovies.map((m) => (
                <MovieCard
                  key={m.id}
                  id={m.id}
                  title={m.title}
                  image={m.image}
                  rating={m.rating}
                  category={m.category}
                  year={m.year}
                  isNew={m.isNew}
                />
              ))}
            </div>
          </section>
        )}

        {/* 内部导航 */}
        <nav className="mt-10 pt-6 border-t border-background-200/60" aria-label="相关内容导航">
          <div className="flex flex-wrap gap-4 text-xs">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              首页
            </Link>
            <Link to={`/category/${movie.category}`} className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              {categoryNames[movie.category] || movie.category}
            </Link>
            <Link to="/about" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
              关于我们
            </Link>
          </div>
        </nav>
      </article>
    </>
  );
}